import { SubAPI } from './API'
import { IChatRoom, IChatMember } from 'types/chatInterfaces'
import { IAccount } from 'types/userInterfaces'

/**
 * Chat API
 * @notExported
 */
class ChatAPI extends SubAPI {
  /**
   * Get all rooms.
   *
   * @returns Array of chat rooms.
   */
  public getRooms(controller?: AbortController): Promise<IChatRoom[]> {
    return this.api.get(`chat/`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get room details by room name.
   *
   * @returns Chat room details.
   */
  public getRoom(roomName: string, controller?: AbortController): Promise<IChatRoom> {
    return this.api.get(`chat/room/${roomName}`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all invites.
   *
   * @returns Array of chat invites.
   */
  public getInvites(controller?: AbortController): Promise<IChatMember[]> {
    return this.api.get(`chat/invites`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get allowed accounts.
   *
   * @returns Array of accounts.
   */
  public getAllowed(controller?: AbortController): Promise<IAccount[]> {
    return this.api.get('chat/allowed', controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Create chat room.
   *
   * @param data - Chat room data.
   * @returns Created chat room.
   */
  public create(data: IChatRoom, controller?: AbortController): Promise<IChatRoom> {
    return this.api.post(`chat/`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Update chat room.
   *
   * @param data - Chat room data.
   * @returns Updated chat room.
   */
  public save(data: IChatRoom, controller?: AbortController): Promise<IChatRoom> {
    return this.api.put(`chat/${data.id}`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Delete chat room.
   *
   * @param roomId - Chat room ID.
   * @returns Promise of room deletion.
   */
  public delete(roomId: number, controller?: AbortController): Promise<void> {
    return this.api.delete(`chat/${roomId}`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Check existence of room.
   *
   * @param roomName - Room name.
   * @returns Promise of room existence.
   */
  public exists(roomName: string, controller?: AbortController): Promise<boolean> {
    return this.api.get(`chat/exists/${roomName}`, controller ? { signal: controller.signal } : undefined)
  }
}

export const chatAPI = new ChatAPI()
